import React, { useRef } from 'react';

/**
 * A file input that looks like the text fields around it.
 *
 * The browser's own control shows "Durchsuchen… Keine Datei ausgewählt" in the
 * platform's styling, which reads as a foreign object in a styled form. Worse
 * is hiding it behind a text link: then nothing on screen says a file is wanted
 * at all.
 */
export function FileField({
  label,
  accept,
  filename,
  placeholder = 'Keine Datei gewählt',
  hint,
  actions,
  onSelect,
  onClear,
}) {
  const inputRef = useRef(null);
  const pick = () => inputRef.current?.click();

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
        {actions?.length > 0 && (
          <div className="flex items-center gap-3">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:disabled:text-slate-500"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* A row of siblings, not one button wrapping the others: a button inside
          a button is invalid and the click would land on both. */}
      <div className="flex w-full items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 transition-colors focus-within:ring-2 focus-within:ring-blue-500 hover:border-blue-400 dark:hover:border-blue-500">
        <button type="button" onClick={pick} className="flex min-w-0 grow items-center gap-3 text-left">
          <svg
            className="w-4 h-4 shrink-0 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <span
            className={`truncate font-mono text-xs ${
              filename ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'
            }`}
          >
            {filename || placeholder}
          </span>
        </button>

        {filename && onClear && (
          <button
            type="button"
            onClick={onClear}
            title="Datei entfernen"
            aria-label="Datei entfernen"
            className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={pick}
          className="shrink-0 rounded-md bg-slate-100 dark:bg-slate-700 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
        >
          Datei wählen
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          onSelect?.(e.target.files?.[0] || null);
          // Clear it, so choosing the same file twice still fires a change.
          e.target.value = '';
        }}
      />

      {hint && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  );
}
